class CartCrossSell extends HTMLElement {
  connectedCallback() {
    const configEl = this.querySelector('script.cart-cs-config');
    if (!configEl) return;

    try {
      this._config = JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('[CartCrossSell] JSON parse error', e);
      return;
    }

    this._index   = 0;
    this._navEl   = this.querySelector('.cart-cs__nav');
    this._prevBtn = this.querySelector('.cart-cs__prev');
    this._nextBtn = this.querySelector('.cart-cs__next');
    this._counter = this.querySelector('.cart-cs__counter');

    this.addEventListener('change', this._onVariantChange.bind(this));
    this.addEventListener('click',  this._onClick.bind(this));

    if (this._config.type === 'recommended') {
      this._fetchRecommended();
    } else {
      this._initCarousel();
    }
  }

  // ── Carousel ───────────────────────────────────────────

  _initCarousel() {
    this._cards = Array.from(this.querySelectorAll('.cart-cs__card'));

    if (this._cards.length === 0) {
      // No products to show — hide entire section
      this.style.display = 'none';
      return;
    }

    this._goTo(0);

    if (this._cards.length > 1 && this._navEl) {
      this._navEl.hidden = false;
      this._updateCounter();
    }
  }

  _goTo(index) {
    this._cards.forEach((card, i) => {
      card.classList.toggle('cart-cs__card--hidden', i !== index);
    });
    this._index = index;
    this._updateCounter();
  }

  _updateCounter() {
    if (this._counter) {
      this._counter.textContent = `${this._index + 1} / ${this._cards.length}`;
    }
    if (this._prevBtn) this._prevBtn.disabled = this._index === 0;
    if (this._nextBtn) this._nextBtn.disabled = this._index === this._cards.length - 1;
  }

  // ── Events ────────────────────────────────────────────

  _onClick(e) {
    if (e.target.closest('.cart-cs__prev')) {
      if (this._index > 0) this._goTo(this._index - 1);
      return;
    }
    if (e.target.closest('.cart-cs__next')) {
      if (this._index < this._cards.length - 1) this._goTo(this._index + 1);
      return;
    }

    const btn = e.target.closest('[data-cs-add]');
    if (!btn || btn.disabled || btn.classList.contains('is-loading')) return;
    const variantId = parseInt(btn.dataset.variantId, 10);
    if (variantId) this._addToCart(btn, variantId);
  }

  _onVariantChange(e) {
    const select = e.target.closest('.cart-cs__variant');
    if (!select) return;

    const card   = select.closest('.cart-cs__card');
    const addBtn = card?.querySelector('[data-cs-add]');
    const opt    = select.options[select.selectedIndex];

    const available = opt.dataset.available === 'true';
    const variantId = parseInt(opt.value, 10);
    const price     = parseInt(opt.dataset.price, 10) || 0;
    const compare   = parseInt(opt.dataset.compareAtPrice, 10) || 0;

    if (addBtn) {
      addBtn.dataset.variantId = variantId;
      addBtn.disabled          = !available;
      addBtn.textContent       = available ? 'Adaugă' : 'Indisponibil';
    }

    const priceEl = card?.querySelector('.cart-cs__price');
    if (priceEl) {
      let html = '';
      if (compare && compare > price) {
        html += `<span class="cart-cs__price-crossed">${this._fmt(compare)}</span>`;
      }
      html += `<span class="cart-cs__price-sale">${this._fmt(price)}</span>`;
      priceEl.innerHTML = html;
    }
  }

  // ── Shopify Recommendations ────────────────────────────

  _fetchRecommended() {
    const { productId, limit } = this._config;
    if (!productId) {
      this.style.display = 'none';
      return;
    }

    fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`)
      .then(r => r.json())
      .then(data => {
        const products = (data.products || []).filter(p => p.available);

        if (!products.length) {
          this.style.display = 'none';
          return;
        }

        const listEl = this.querySelector('#cart-cs-list');
        if (!listEl) return;

        listEl.innerHTML = products.map((p, i) => this._buildCard(p, i)).join('');
        this._initCarousel();
      })
      .catch(err => {
        console.error('[CartCrossSell] Recommendations fetch error:', err);
        this.style.display = 'none';
      });
  }

  _buildCard(p, index) {
    const pv          = p.variants.find(v => v.available) || p.variants[0];
    const hidden      = index > 0 ? ' cart-cs__card--hidden' : '';
    const imgTag      = p.featured_image
      ? `<img src="${p.featured_image.replace(/(\.[^.]+)$/, '_120x120$1')}" alt="${this._esc(p.title)}" width="60" height="60" loading="lazy">`
      : '';
    const priceHtml   = this._buildPriceHtml(pv.compare_at_price, pv.price);
    const variantHtml = p.variants.length > 1 ? this._buildVariantSelect(p.variants, pv.id) : '<span></span>';
    const unavailable = !pv.available;

    return `
      <div class="cart-cs__card${hidden}">
        <a class="cart-cs__thumb" href="${p.url}">${imgTag}</a>
        <div class="cart-cs__body">
          <div class="cart-cs__top-row">
            <p class="cart-cs__name">${this._esc(p.title)}</p>
            <div class="cart-cs__price">${priceHtml}</div>
          </div>
          <div class="cart-cs__bottom-row">
            ${variantHtml}
            <button class="cart-cs__add" data-cs-add data-variant-id="${pv.id}" type="button" ${unavailable ? 'disabled' : ''}>
              ${unavailable ? 'Indisponibil' : 'Adaugă'}
            </button>
          </div>
        </div>
      </div>`;
  }

  _buildPriceHtml(compare, price) {
    let html = '';
    if (compare && compare > price) {
      html += `<span class="cart-cs__price-crossed">${this._fmt(compare)}</span>`;
    }
    html += `<span class="cart-cs__price-sale">${this._fmt(price)}</span>`;
    return html;
  }

  _buildVariantSelect(variants, selectedId) {
    const options = variants.map(v => {
      const label = v.available ? v.title : `${v.title} — Stoc epuizat`;
      return `<option value="${v.id}" data-price="${v.price}" data-compare-at-price="${v.compare_at_price || 0}" data-available="${v.available}" ${v.id === selectedId ? 'selected' : ''} ${!v.available ? 'disabled' : ''}>${label}</option>`;
    }).join('');
    return `<select class="cart-cs__variant">${options}</select>`;
  }

  // ── Add to cart ────────────────────────────────────────

  _addToCart(btn, variantId) {
    btn.classList.add('is-loading');
    btn.disabled = true;

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const payload = {
      items: [{ id: variantId, quantity: 1 }],
      sections: sectionsToBundle,
      sections_url: window.location.pathname,
    };

    const config  = fetchConfig('javascript');
    config.method = 'POST';
    config.body   = JSON.stringify(payload);

    const { MinimogSettings, MinimogStrings } = window;

    fetch(`${MinimogSettings.routes.cart_add_url}`, config)
      .then(r => r.json())
      .then(response => {
        if (response.status) {
          console.error('[CartCrossSell] Add to cart error:', response.description);
          return;
        }

        const cartDrawer = document.querySelector('m-cart-drawer');

        if (MinimogSettings.use_ajax_atc) {
          if (cartDrawer && MinimogSettings.enable_cart_drawer) {
            window.MinimogTheme.Notification.show({
              target: cartDrawer.querySelector('m-cart-items'),
              method: 'prepend',
              type: 'success',
              message: MinimogStrings.itemAdded,
              delay: 400,
            });
          } else {
            window.MinimogTheme.Notification.show({
              target: document.body,
              method: 'appendChild',
              type: 'success',
              message: MinimogStrings.itemAdded,
              last: 3000,
              sticky: true,
            });
          }

          window.MinimogEvents.emit(
            MinimogTheme.pubSubEvents.cartUpdate,
            { cart: response }
          );

          document.dispatchEvent(
            new CustomEvent('product-ajax:added', {
              detail: { product: response },
            })
          );
        } else {
          window.location = MinimogSettings.routes.cart;
        }
      })
      .catch(err => {
        console.error('[CartCrossSell] Fetch error:', err);
      })
      .finally(() => {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      });
  }

  // ── Helpers ────────────────────────────────────────────

  _fmt(cents) {
    const format = window.MinimogSettings?.money_format;
    if (typeof formatMoney === 'function' && format) {
      return formatMoney(cents, format);
    }
    return (cents / 100).toFixed(2);
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

customElements.define('cart-cross-sell', CartCrossSell);
